import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth, homeFor } from "@/context/AuthContext";
import { Loading } from "@/components/Common";
import CustomerLayout from "@/layouts/CustomerLayout";
import AdminLayout from "@/layouts/AdminLayout";
import { Login, Register, ForgotPassword, ResetPassword, Privacy } from "@/pages/Auth";
import CustomerDashboard from "@/pages/customer/Dashboard";
import NewRequest from "@/pages/customer/NewRequest";
import { RequestDetail as CRequest, History, JobDetail as CJob } from "@/pages/customer/Requests";
import { Appointments as CAppointments, BookAppointment } from "@/pages/customer/Appointments";
import { Quotations as CQuotes, QuotationDetail } from "@/pages/customer/Quotations";
import Profile from "@/pages/customer/Profile";
import Start from "@/pages/Start";
import { ChangePassword } from "@/components/ChangePassword";
import { PageHeader, Section, Detail } from "@/components/Common";

function AdminAccount() {
  const { user } = useAuth();
  return (
    <div className="max-w-2xl space-y-5">
      <PageHeader title="My account" />
      <Section title="Signed in as"><dl className="grid gap-3 sm:grid-cols-3"><Detail label="Name">{user.name}</Detail><Detail label="Email">{user.email}</Detail><Detail label="Role"><span className="capitalize">{user.role}</span></Detail></dl></Section>
      <ChangePassword admin />
    </div>
  );
}
import Overview from "@/pages/admin/Overview";
import { Requests, RequestDetail } from "@/pages/admin/Requests";
import { Appointments, CalendarPage } from "@/pages/admin/Appointments";
import { Customers, CustomerDetail } from "@/pages/admin/Customers";
import { Quotations, QuotationEditor } from "@/pages/admin/Quotations";
import { Jobs, JobDetail } from "@/pages/admin/Jobs";
import { Services, Availability, SettingsPage, Users, Notifications } from "@/pages/admin/Config";

function Guard({ roles, children }) {
  const { user } = useAuth();
  if (user === null) return <div className="p-8"><Loading /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) return <Navigate to={homeFor(user)} replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <div className="p-8"><Loading /></div>;
  const next = new URLSearchParams(location.search).get("next");
  const dest = user?.role === "customer" && next?.startsWith("/portal") ? next : homeFor(user);
  return user ? <Navigate to={dest} replace /> : children;
}

function Root() {
  const { user } = useAuth();
  if (user === null) return <div className="p-8"><Loading /></div>;
  return user ? <Navigate to={homeFor(user)} replace /> : <Start />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Root />} />
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/portal" element={<Guard roles={["customer"]}><CustomerLayout /></Guard>}>
            <Route index element={<CustomerDashboard />} />
            <Route path="new" element={<NewRequest />} />
            <Route path="requests/:id" element={<CRequest />} />
            <Route path="appointments" element={<CAppointments />} />
            <Route path="appointments/book" element={<BookAppointment />} />
            <Route path="quotations" element={<CQuotes />} />
            <Route path="quotations/:id" element={<QuotationDetail />} />
            <Route path="history" element={<History />} />
            <Route path="jobs/:id" element={<CJob />} />
            <Route path="profile" element={<Profile />} />
          </Route>
          <Route path="/admin" element={<Guard roles={["admin", "staff"]}><AdminLayout /></Guard>}>
            <Route index element={<Overview />} />
            <Route path="requests" element={<Requests />} />
            <Route path="requests/:id" element={<RequestDetail />} />
            <Route path="appointments" element={<Appointments />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="customers" element={<Customers />} />
            <Route path="customers/:id" element={<CustomerDetail />} />
            <Route path="quotations" element={<Quotations />} />
            <Route path="quotations/new" element={<QuotationEditor />} />
            <Route path="quotations/:id" element={<QuotationEditor />} />
            <Route path="jobs" element={<Jobs />} />
            <Route path="jobs/:id" element={<JobDetail />} />
            <Route path="services" element={<Services />} />
            <Route path="availability" element={<Availability />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="users" element={<Users />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="account" element={<AdminAccount />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
